/* eslint-disable max-lines-per-function */
import {
  EarningsItemWithEntity,
  EconomicEventOutput,
  KeyDevsObjectWithEntity,
} from '@api';
import {
  Icon,
  Tooltip,
  TooltipTrigger,
  WithNAFallback,
} from '@design';
import { getPriceStatus } from '@helpers';
import { ColumnDef } from '@tanstack/react-table';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { getFormattedDates } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { enUS } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '../dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import sharedStyles from './calendar-shared-styles/CalendarSharedStyles.module.scss';
import { EarningInsightsCell } from './events-table/components/earning-insights-cell/EarningInsightsCell';
import tableStyles from './events-table/EventsTable.module.scss';
import { TabsType } from './utils';

const SORT_UNDEFINED = 'last';
const EARNINGS_INSIGHTS_COLUMN_ID = 'earning_insights';

interface UseEventsColumnsProps {
  activeTab: string;
}

export const useEventsColumns = ({ activeTab }: UseEventsColumnsProps) => {
  const { t } = useTranslation(['calendar']);
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const earningInsights: ColumnDef<EarningsItemWithEntity>[] = useMemo(
    () =>
      isBasicOrLightUser
        ? []
        : [
            {
              id: EARNINGS_INSIGHTS_COLUMN_ID,
              header: t('calendar:table.earningInsights'),
              cell: props => (
                <EarningInsightsCell
                  earningsId={props.row.original.id}
                  entityTag={props.row.original.entity?.tag}
                />
              ),
              accessorFn: row => row.reporting_date,
              sortingFn: 'datetime',
              sortUndefined: SORT_UNDEFINED,
            },
          ],
    [isBasicOrLightUser]
  );

  const earningsColumns: ColumnDef<EarningsItemWithEntity>[] = useMemo(
    () => [
      {
        id: 'reporting_date',
        header: t('calendar:table.time'),
        cell: props => {
          const { reporting_date: reportingDate } = props.row.original;
          const date = new Date(reportingDate);
          const { isBeforeNineThirtyAm, isAfterFourPm } =
            getFormattedDates(date);

          return (
            <WithNAFallback
              value={
                date !== undefined && (
                  <div className={tableStyles.timeWrapper}>
                    <Tooltip
                      isTouchDevice={false}
                      trigger={TooltipTrigger.Hover}
                      label={
                        <span>
                          {formatInTimeZone(
                            date,
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                            'h:mmaaa zz',
                            {
                              locale: enUS,
                            }
                          )}
                        </span>
                      }
                    >
                      <div className={tableStyles.timeIconWrapper}>
                        {isBeforeNineThirtyAm && (
                          <Icon
                            iconName="SunRise"
                            size={16}
                            className={sharedStyles.sunRise}
                          />
                        )}
                        {isAfterFourPm && (
                          <Icon
                            iconName="Moon"
                            size={16}
                            className={sharedStyles.moon}
                          />
                        )}
                        {!isBeforeNineThirtyAm && !isAfterFourPm && (
                          <Icon
                            iconName="Sun"
                            size={16}
                            className={sharedStyles.sun}
                          />
                        )}
                      </div>
                    </Tooltip>
                    {format(date, 'HH:mm')}
                  </div>
                )
              }
            />
          );
        },
        accessorFn: row => new Date(row.reporting_date),
        sortingFn: 'datetime',
      },
      {
        id: 'entity',
        header: t('calendar:table.asset'),
        cell: props => {
          const { entity } = props.row.original;

          return entity?.tag && <EntityCell entityTag={entity.tag} />;
        },
        accessorFn: row =>
          row.entity?.name_short || row.entity?.name_full || row.entity?.name,
        sortUndefined: SORT_UNDEFINED,
      },
      ...earningInsights,
      {
        id: 'eps.estimate',
        header: t('calendar:table.expectedEps'),
        cell: props => {
          const {
            eps: { estimate },
          } = props.row.original;

          return (
            <WithNAFallback
              value={
                estimate !== undefined && (
                  <span>
                    {formatNumber({ isPercentage: false, value: estimate })}
                  </span>
                )
              }
            />
          );
        },
        accessorFn: row => row.eps.estimate,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'reported',
        header: t('calendar:table.reported'),
        cell: props => {
          const {
            eps: { value },
          } = props.row.original;

          return (
            <WithNAFallback
              value={
                value !== undefined && (
                  <span>{formatNumber({ isPercentage: false, value })}</span>
                )
              }
            />
          );
        },
        accessorFn: row => row.eps.value,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'revenue.surprise',
        header: t('calendar:table.surpriseAmount'),
        cell: props => {
          const {
            eps: { surprise },
          } = props.row.original;

          return (
            <WithNAFallback
              value={
                surprise !== undefined && (
                  <div
                    className={clsx(
                      tableStyles.surpriseWrapper,
                      tableStyles[getPriceStatus(surprise)]
                    )}
                  >
                    {formatNumber({ isPercentage: false, value: surprise })}
                  </div>
                )
              }
            />
          );
        },
        accessorFn: row => row.eps.surprise,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'revenue.surprise_percentage',
        header: t('calendar:table.surprisePercentage'),
        cell: props => {
          const {
            eps: { surprise_percentage: surprisePercentage },
          } = props.row.original;

          return (
            <WithNAFallback
              value={
                surprisePercentage !== undefined && (
                  <div
                    className={clsx(
                      tableStyles.surpriseWrapper,
                      tableStyles[getPriceStatus(surprisePercentage)]
                    )}
                  >
                    {formatNumber({
                      isPercentage: true,
                      value: surprisePercentage,
                      addPlus: true,
                    })}
                  </div>
                )
              }
            />
          );
        },
        accessorFn: row => row.eps.surprise_percentage,
        sortUndefined: SORT_UNDEFINED,
      },
    ],
    [earningInsights]
  );

  const economicsColumns: ColumnDef<EconomicEventOutput>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t('calendar:table.time'),
        cell: props => {
          const { date } = props.row.original;

          return (
            <WithNAFallback
              check={!!date}
              value={<span>{format(new Date(date), 'HH:mm')}</span>}
            />
          );
        },
        accessorFn: row => new Date(row.date),
        sortingFn: 'datetime',
      },
      {
        id: 'name',
        header: t('calendar:table.eventName'),
        cell: props => {
          const { name, country } = props.row.original;

          return (
            <WithNAFallback
              check={!!name && !!country}
              value={
                <div className={tableStyles.eventNameWrapper}>
                  <FlagIcon
                    countryCode={country}
                    variant="circular"
                    customSize={20}
                  />
                  <span className={tableStyles.nortFontText}>{name}</span>
                </div>
              }
            />
          );
        },
        accessorFn: row => row.name,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'category',
        header: t('calendar:table.eventCategory'),
        cell: props => {
          const { category } = props.row.original;

          return (
            <WithNAFallback
              check={!!category}
              value={
                <span className={tableStyles.nortFontText}>{category}</span>
              }
            />
          );
        },
        accessorFn: row => row.category,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'period',
        header: t('calendar:table.refPeriod'),
        cell: props => {
          const { period } = props.row.original;

          return (
            <WithNAFallback
              check={!!period}
              value={<span className={tableStyles.nortFontText}>{period}</span>}
            />
          );
        },
        accessorFn: row => row.period,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'consensus',
        header: t('calendar:table.expected'),
        cell: props => {
          const { consensus } = props.row.original;

          return (
            <WithNAFallback
              value={
                consensus !== null &&
                consensus !== undefined && (
                  <span>
                    {formatNumber({ isPercentage: false, value: consensus })}
                  </span>
                )
              }
            />
          );
        },
        accessorFn: row => (row.consensus === null ? undefined : row.consensus),
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'reported',
        header: t('calendar:table.reported'),
        cell: props => {
          const { actual_unit: actualUnit, actual } = props.row.original;
          const isPercentage = actualUnit === '%';

          return (
            <WithNAFallback
              value={
                actual !== undefined &&
                actual !== null && (
                  <span>
                    {formatNumber({
                      isPercentage,
                      value: actual,
                      addPlus: isPercentage,
                    })}
                  </span>
                )
              }
            />
          );
        },
        accessorFn: row => row.actual,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'surprise',
        header: t('calendar:table.surprise'),
        cell: props => {
          const {
            surprise_unit: surpriseUnit,
            actual_unit: actualUnit,
            surprise,
          } = props.row.original;

          const isPercentage = (surpriseUnit ?? actualUnit) === '%';

          return (
            <WithNAFallback
              value={
                surprise !== undefined &&
                surprise !== null && (
                  <div
                    className={clsx(
                      tableStyles.surpriseWrapper,
                      tableStyles[getPriceStatus(surprise)]
                    )}
                  >
                    {formatNumber({
                      isPercentage,
                      value: surprise,
                      addPlus: isPercentage,
                    })}
                  </div>
                )
              }
            />
          );
        },
        accessorFn: row => row.surprise,
        sortUndefined: SORT_UNDEFINED,
      },
    ],
    []
  );

  const companyColumns: ColumnDef<KeyDevsObjectWithEntity>[] = useMemo(
    () => [
      {
        id: 'effectiveDate',
        header: t('calendar:table.time'),
        cell: props => {
          const { effective_date: effectiveDate } = props.row.original;

          return (
            <WithNAFallback
              check={!!effectiveDate}
              value={
                <div className={tableStyles.timeWrapper}>
                  {format(new Date(effectiveDate), 'HH:mm')}
                </div>
              }
            />
          );
        },
        accessorFn: row => new Date(row.effective_date),
        sortingFn: 'datetime',
      },
      {
        id: 'ticker',
        header: t('calendar:table.asset'),
        cell: props => {
          const { entity } = props.row.original;

          return (
            <WithNAFallback
              check={!!entity?.tag}
              value={entity?.tag && <EntityCell entityTag={entity?.tag} />}
            />
          );
        },
        accessorFn: row => row.entity?.name ?? row.entity?.tag,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'eventType',
        header: t('calendar:table.eventType'),
        cell: props => {
          const { event_type: eventType } = props.row.original;

          return (
            <WithNAFallback
              value={
                <span className={tableStyles.nortFontText}>{eventType}</span>
              }
            />
          );
        },
        accessorFn: row => row.event_type,
        sortUndefined: SORT_UNDEFINED,
      },
      {
        id: 'headline',
        header: t('calendar:table.headline'),
        cell: props => {
          const { headline } = props.row.original;

          return (
            <WithNAFallback
              check={!!headline}
              value={<span className={tableStyles.headline}>{headline}</span>}
            />
          );
        },
        accessorFn: row => row.headline,
        sortUndefined: SORT_UNDEFINED,
      },
    ],
    []
  );

  switch (activeTab) {
    default:
      return earningsColumns;
    case TabsType.Economics:
      return economicsColumns;
    case TabsType.Company:
      return companyColumns;
  }
};
